// src/pages/EditInvoice.jsx
import React, { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

// master lists (same as CreateInvoice)
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import { useGetAllProductsQuery } from "../toolkitServices/productApi";
import { useGetAllDesignsQuery } from "../toolkitServices/designApi";
import { useGetAllPuritiesQuery } from "../toolkitServices/purityApi";
import { useGetAllStonesQuery } from "../toolkitServices/stoneApi";
import { useGetAllDiamondsQuery } from "../toolkitServices/diamondApi";
import { useGetDiamondSievesQuery } from "../toolkitServices/diamondSieveApi";
import { useGetDiamondSizesQuery } from "../toolkitServices/diamondSizeApi";
import { useGetCountersQuery } from "../toolkitServices/counterApi";

// store setters (same as CreateInvoice)
import { setCategoryList } from "../slices/categorySlice";
import { setProductList } from "../slices/productSlice";
import { setDesignList } from "../slices/designSlice";
import { setPurityList } from "../slices/puritySlice";
import { setBranchList } from "../slices/branchSlice";
import { setStoneList } from "../slices/stoneSlice";
import { setDiamondList } from "../slices/diamondSlice";
import { setDiamondSieveList } from "../slices/diamondSieveSlice";
import { setDiamondSizeList } from "../slices/diamondSizeSlice";
import { setCounterList } from "../slices/counterSlice";

import { useGetInvoiceByIdQuery } from "../toolkitServices/invoiceApi";
import InvoiceForm from "../components/NewInvoice/InvoiceForm";
import { useGetEstimateByIdQuery } from "../toolkitServices/estimateApi";
import EstimateForm from "../components/Estimate/EstimateForm";

/* ------------------- helpers ------------------- */
const listFrom = (resp) =>
  resp?.data ?? resp?.rows ?? resp?.items ?? resp ?? [];

const mkOpt = (doc, labelKey = "name") =>
  doc
    ? {
        label: doc[labelKey] || doc.name || "",
        value: doc._id || doc.id || "",
        info: doc,
      }
    : null;

const findById = (arr = [], id) =>
  arr?.find?.((x) => String(x._id || x.id || x.value) === String(id));

const findBranch = (list, id) =>
  mkOpt(findById(list, id) || { _id: id, branchName: "" }, "branchName");
const findCounter = (list, id) =>
  mkOpt(findById(list, id) || { _id: id, counterName: "" }, "counterName");
const findCat = (list, id) =>
  mkOpt(findById(list, id) || { _id: id, categoryName: "" }, "categoryName");
const findProd = (list, id) =>
  mkOpt(findById(list, id) || { _id: id, productName: "" }, "productName");
const findDesign = (list, id) =>
  mkOpt(findById(list, id) || { _id: id, designName: "" }, "designName");
const findPurity = (list, id) =>
  mkOpt(findById(list, id) || { _id: id, purityName: "" }, "purityName");
const findStone = (list, id) =>
  mkOpt(findById(list, id) || { _id: id, stoneName: "" }, "stoneName");
const findDiamond = (list, id) =>
  mkOpt(findById(list, id) || { _id: id, diamondName: "" }, "diamondName");

// if option missing label, make a safe fallback so react-select shows something
const ensureOpt = (opt, id, fallbackLabel = "") => {
  const label =
    opt?.label ||
    fallbackLabel ||
    (typeof id === "string" ? `#${id.slice(-6)}` : "Unknown");
  const value = opt?.value ?? id ?? "";
  const info = opt?.info ?? (id ? { _id: id, name: label } : {});
  return { label, value, info };
};

/* ----------------------- API doc -> Form defaultValues ------------------------ */
function toFormShape(doc, master) {
  const {
    categories,
    products,
    designs,
    purities,
    branches,
    stones,
    diamonds,
    counters,
  } = master;

  const branch = findBranch(branches, doc.branchId);

  // customer as {label,value,info}
  const customer = {
    label: doc?.customer?.customerId || "",
    value: doc?.customer?._id || "",
    info: doc?.customer || {},
  };

  const stockEntries = (doc.stockEntries || []).map((r) => {
    const counterOpt = r?.counterId ? findCounter(counters, r.counterId) : null;

    const stonesArr = (r.stones || []).map((s) => {
      return {
        // IMPORTANT: keep ID (popup expects ID string)
        stnPopupName: s?.stoneId,
        unit: s.unit || "",
        shape: s.shape || null,
        color: s.color || null,
        size: s.size || null,
        settingType: s.settingType || null,
        stnPopupWt: s.wt,
        stnPopupRateByWt: s.rateByWt,
        stnPopupPcs: s.pcs,
        stnPopupRateByPcs: s.rateByPcs,
        stnPopupAmt: s.amount,
        stnPopupDeduct: s.deduct || "",
        stnPopupDescription: s.description || "",
      };
    });

    const diamondsArr = (r.diamonds || []).map((d) => {
      // IMPORTANT: pass ID (popup expects ID string)
      return {
        diaPopupName: d?.diamondId,
        unit: d.unit || "carat",
        shape: d.shape || null,
        color: d.color || null,
        clarity: d.clarity || null,
        sieve: d.sieve || null,
        size: d.size || null,
        diaPopupWt: d.ct,
        diaPopupRateByCt: d.rateByCt,
        diaPopupPcs: d.pcs,
        diaPopupRateByPcs: d.rateByPcs,
        diaPopupAmt: d.amount,
        diaPopupDeduct: d.deduct || "",
        diaPopupDescription: d.description || "",
      };
    });

    return {
      // labelled item (optional)
      itemCodeDetail: r.itemCode
        ? {
            label: r.itemCode,
            value: r.labelledItemId,
            info: { _id: r.labelledItemId },
          }
        : null,

      // dropdowns
      category: r.categoryId
        ? { value: r.categoryId, label: r.categoryName }
        : null,
      product: r.productId
        ? { value: r.productId, label: r.productName }
        : null,
      design: r.designId ? { value: r.designId, label: r.designName } : null,
      purity: r.purityId ? { value: r.purityId, label: r.purityName } : null,

      counterRow: counterOpt
        ? ensureOpt(counterOpt, r.counterId, r.counterName)
        : null,

      // numbers/snapshot
      qty: r.qty,
      totalWt: r.totalWt,
      packingWt: r.packingWt,
      grossWt: r.grossWt,
      stoneWt: r.stoneWt,
      diamondWt: r.diamondWt,
      netWt: r.netWt,
      stonePcs: r.stonePcs,
      diamondPcs: r.diamondPcs,
      ratePerGram: r.ratePerGram,
      makingPercentage: r.makingPercentage,
      makingPerGram: r.makingPerGram,
      fixedWastage: r.fixedWastage,
      fixedAmount: r.fixedAmount,
      stoneAmount: r.stoneAmount,
      diamondAmount: r.diamondAmount,
      hallmarkAmount: r.hallmarkAmount,
      itemAmount: r.itemAmount,
      itemGstAmount: r.itemGstAmount,
      totalItemAmount: r.totalItemAmount,
      mrp: r.mrp,
      huidCode: r.huidCode,
      description: r.description,
      soldBy: r?.soldById ? { value: r?.soldById, label: r?.soldByName } : null,
      commissionPercent: r?.commissionPercent,
      commissionAmount: r?.commissionAmount,
      // popups
      stones: stonesArr,
      diamonds: diamondsArr,
    };
  });

  const looseStones = (doc.looseStones || []).map((s) => {
    const stoneOpt = s.stoneId
      ? ensureOpt(findStone(stones, s.stoneId), s.stoneId, s.stoneName)
      : null;
    const counterOpt = s.counterId
      ? ensureOpt(findCounter(counters, s.counterId), s.counterId)
      : null;

    return {
      counterRow: counterOpt,
      // if your loose stone form also uses the same popup select, keep ID not object.
      stnPopupName: s?.stoneId ?? stoneOpt?.value ?? null,
      unit: s.unit || "",
      shape: s.shape || null,
      color: s.color || null,
      size: s.size || null,
      settingType: s.settingType || null,
      stnPopupWt: s.wt,
      stnPopupPcs: s.pcs,
      itemAmount: s.itemAmount,
      itemGstAmount: s.itemGstAmount,
      totalItemAmount: s.totalItemAmount,
      stnPopupDescription: s.description || "",
    };
  });

  const looseDiamonds = (doc.looseDiamonds || []).map((d) => {
    const counterOpt = d.counterId
      ? ensureOpt(findCounter(counters, d.counterId), d.counterId)
      : null;

    return {
      counterRow: counterOpt,
      // IMPORTANT: pass ID (not {label,value})
      diaName: d?.diamondId ?? null,
      unit: d.unit || "carat",
      shape: d.shape || null,
      color: d.color || null,
      clarity: d.clarity || null,
      sieve: d.sieve || null,
      size: d.size || null,
      diaWt: d.ct,
      pcs: d.pcs,
      itemAmount: d.itemAmount,
      itemGstAmount: d.itemGstAmount,
      totalItemAmount: d.totalItemAmount,
      description: d.description || "",
    };
  });

  const payments = (doc.payments || []).map((p) => ({
    type: p.type || "Receipt",
    mode: p.mode || "Cash",
    amount: Number(p.amount || 0).toFixed(2),
    refNo: p.refNo || "",
    description: p.description || "",
    sourceType: "INVOICE",
  }));

  return {
    _id: doc._id,
    customer,
    branch,
    invoiceNo: String(doc.invNo ?? ""),
    invoiceDate: (doc.invDate || "").slice(0, 10),

    stockEntries,
    looseStones,
    looseDiamonds,
    payments,

    gstApply: !!doc.gstApply,
    gstRoundOff: !!doc.gstRoundOff,
    tdsApply: !!doc.tdsApply,

    totalDiscount: doc.totals?.discount || "",
    courierCharge: doc.totals?.courier || "",

    // UI-only mirrors (filled when customer picked)
    fineGold: "",
    fineSilver: "",
    advanceAmount: "",
    balanceAmount: "",
    grandTotalManual: "",
  };
}

/* --------------------------------- Component --------------------------------- */
const EditEstimate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: invoiceResp, isFetching } = useGetEstimateByIdQuery(id);

  // masters: fetch & put into store (normalized)
  const { data: branches } = useGetAllBranchesQuery();
  const { data: categories } = useGetAllCategoriesQuery({
    page: 1,
    limit: 1000,
    search: "",
  });
  const { data: products } = useGetAllProductsQuery({
    page: 1,
    limit: 2000,
    search: "",
  });
  const { data: designs } = useGetAllDesignsQuery({
    page: 1,
    limit: 2000,
    search: "",
  });
  const { data: purities } = useGetAllPuritiesQuery({
    page: 1,
    limit: 1000,
    search: "",
  });
  const { data: stones } = useGetAllStonesQuery({
    page: 1,
    limit: 2000,
    search: "",
  });
  const { data: diamonds } = useGetAllDiamondsQuery({
    page: 1,
    limit: 2000,
    search: "",
  });
  const { data: diamondSieves } = useGetDiamondSievesQuery({
    page: 1,
    limit: 100,
  });
  const { data: diamondSizes } = useGetDiamondSizesQuery({
    page: 1,
    limit: 100,
  });
  const { data: counters } = useGetCountersQuery({ page: 1, limit: 2000 });

  useEffect(() => {
    const cats = listFrom(categories);
    const prods = listFrom(products);
    const dsgs = listFrom(designs);
    const purs = listFrom(purities);
    const brs = listFrom(branches);
    const stns = listFrom(stones);
    const dias = listFrom(diamonds);
    const sieves = listFrom(diamondSieves);
    const sizes = listFrom(diamondSizes);
    const cnts = listFrom(counters);

    if (cats.length) dispatch(setCategoryList(cats));
    if (prods.length) dispatch(setProductList(prods));
    if (dsgs.length) dispatch(setDesignList(dsgs));
    if (purs.length) dispatch(setPurityList(purs));
    if (brs.length) dispatch(setBranchList(brs));
    if (stns.length) dispatch(setStoneList(stns));
    if (dias.length) dispatch(setDiamondList(dias));
    if (sieves.length) dispatch(setDiamondSieveList(sieves));
    if (sizes.length) dispatch(setDiamondSizeList(sizes));
    if (cnts.length) dispatch(setCounterList(cnts));
  }, [
    categories,
    products,
    designs,
    purities,
    branches,
    stones,
    diamonds,
    diamondSieves,
    diamondSizes,
    counters,
    dispatch,
  ]);

  // keyboard: Ctrl+Backspace -> back
  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey && e.key === "Backspace") {
        e.preventDefault();
        navigate(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  // take masters from store
  const categoriesState = useSelector((s) => s.categories?.categories || []);
  const productsState = useSelector((s) => s.products?.products || []);
  const designsState = useSelector((s) => s.designs?.designs || []);
  const puritiesState = useSelector((s) => s.purities?.purities || []);
  const branchesState = useSelector((s) => s.branches?.branches || []);
  const stonesState = useSelector((s) => s.stones?.stones || []);
  const diamondsState = useSelector((s) => s.diamonds?.diamonds || []);
  const countersState = useSelector((s) => s.counters?.counters || []);

  const existingData = useMemo(() => {
    const doc = invoiceResp?.data || invoiceResp;
    if (!doc) return null;
    if (!categoriesState.length || !productsState.length) return null;

    return toFormShape(doc, {
      categories: categoriesState,
      products: productsState,
      designs: designsState,
      purities: puritiesState,
      branches: branchesState,
      stones: stonesState,
      diamonds: diamondsState,
      counters: countersState,
    });
  }, [invoiceResp]);

  if (isFetching) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="max-w-8xl mx-auto rounded-xl relative max-h-[90vh] overflow-y-auto"
    >
      {existingData && (
        <EstimateForm
          existingData={existingData}
          mode="edit"
          itemId={existingData._id}
          allowMultipleRows={true}
        />
      )}
    </motion.div>
  );
};

export default EditEstimate;
